import clsx from "clsx";
import React from "react";
import Button from "../../../ui/button";
import styles from "./styles.module.scss";
import { useChatbotConfig } from "../../../../contexts/chatbot-config";
import { useBottomSheet } from "../../../../contexts/bottom-sheet";
import { trackLinkClick, isDirectNavigationLink } from "../../../../libs/fns";
import { downloadCtaFiles } from "../../../../libs/cta";

type Props = {
  downloadForm: boolean;
  downloadUrl: string;
};

// イメージキャッシュ（コンポーネント外で管理して永続化）
const imageCache = new Map<string, boolean>();

export default function Cta(_props: Props) {
  const { bottomViewData, chatbotData, screen2HeaderText, ctaData } = useChatbotConfig();
  const { openBottomSheet } = useBottomSheet();

  // 各画像の読み込み状態を管理
  const [loadedImages, setLoadedImages] = React.useState<Set<string>>(new Set());

  // APIデータ構造: bottom_view_data.data配列 or 配列の場合は最初の要素
  const bottomViewObj = Array.isArray(bottomViewData) ? bottomViewData[0] : bottomViewData;
  const bottomData = bottomViewObj?.data || [];
  const leadText = bottomViewObj?.title || screen2HeaderText || ctaData?.title || "各種資料のご案内";

  // data_sortでソート
  const sortedData = [...bottomData].sort((a, b) => {
    const sortA = typeof a.data_sort === 'string' ? parseInt(a.data_sort) : a.data_sort;
    const sortB = typeof b.data_sort === 'string' ? parseInt(b.data_sort) : b.data_sort;
    return (sortA || 0) - (sortB || 0);
  });

  // S3ベースURL
  const baseUrl = chatbotData?.chatbot_S3_URL || "";
  const encodeS3Path = (path: string) => {
    if (!path) return "";
    return path.split('/').map(part => encodeURIComponent(part)).join('/');
  };

  // 画像をプリロード
  React.useEffect(() => {
    sortedData.forEach((item) => {
      const hasImage = item.image && item.image.trim() !== "";
      if (hasImage && item.image) {
        const imageSrc = !item.image.startsWith('http')
          ? `${baseUrl}/${encodeS3Path(item.image)}`
          : item.image;

        // 空文字列チェック
        if (!imageSrc) return;

        // キャッシュに既にある場合は即座に読み込み済みとする
        if (imageCache.has(imageSrc)) {
          setLoadedImages(prev => {
            if (prev.has(imageSrc)) return prev; // 既に追加済みなら何もしない
            const newSet = new Set(prev);
            newSet.add(imageSrc);
            return newSet;
          });
        } else {
          // 新しい画像をプリロード
          const img = new Image();
          img.onload = () => {
            imageCache.set(imageSrc, true);
            setLoadedImages(prev => {
              if (prev.has(imageSrc)) return prev; // 既に追加済みなら何もしない
              const newSet = new Set(prev);
              newSet.add(imageSrc);
              return newSet;
            });
          };
          img.onerror = () => {
            // エラーでもボタンを表示（テキストフォールバック）
            setLoadedImages(prev => {
              if (prev.has(imageSrc)) return prev; // 既に追加済みなら何もしない
              const newSet = new Set(prev);
              newSet.add(imageSrc);
              return newSet;
            });
          };
          img.src = imageSrc;
        }
      }
    });
  }, [sortedData, baseUrl]);

  function setForm(index: number) {
    // Contextの openBottomSheet を使用してフォームを表示（インデックスも渡す）
    openBottomSheet("FORM", index);
  }

  function handleDownLoad(index: number) {
    // Contextの openBottomSheet を使用してダウンロードフォームを表示（インデックスも渡す）
    openBottomSheet("DOWNLOAD", index);
  }

  // リンクがある場合、新しいタブで開き、リード分析APIを呼び出す
  // モバイルSafari対策: 先にwindow.open()を実行してからAPI呼び出し
  function handleLinkClick(link: string) {
    if (link && link.trim() !== "") {
      // tel:/mailto:/sms: の場合はネイティブハンドラーへ直接遷移させる
      if (isDirectNavigationLink(link)) {
        window.location.href = link;

        trackLinkClick(link).catch(error => {
          console.error("リード分析API呼び出しエラー:", error);
        });
        return;
      }

      // ユーザーアクションコンテキスト内で即座に新しいタブを開く
      window.open(link, "_blank", "noopener,noreferrer");

      // その後、非同期でリード分析APIを呼び出す（ブロッキングなし）
      trackLinkClick(link).catch(error => {
        console.error("リード分析API呼び出しエラー:", error);
      });
    }
  }

  return (
    <div className={styles.cta}>
      <p className={styles.lead}>
        {leadText.length > 20 ? (
          <>
            {leadText.slice(0, 20)}
            <br />
            {leadText.slice(20)}
          </>
        ) : (
          leadText
        )}
      </p>
      {ctaData?.description && <p className={styles.description}>{ctaData.description}</p>}
      <div className={clsx(styles.list, sortedData.length === 2 && styles.two)}>
      {sortedData.map((item, index) => {
        const hasImage = item.image && item.image.trim() !== "";
        const imageSrc = hasImage && item.image && !item.image.startsWith('http')
          ? `${baseUrl}/${encodeS3Path(item.image)}`
          : (item.image || "");

        // 画像が読み込まれていない場合はボタンを非表示または透明化
        const isImageLoaded = !hasImage || loadedImages.has(imageSrc);

        // type=1の場合はフォーム表示、dl_type=1の場合はダウンロードフォーム表示
        const itemType = (item as any).type;
        const itemDlType = (item as any).dl_type;
        const itemLink = (item as any).link;
        const itemPage = (item as any).page;
        const itemTitle = item.title || "";
        const isFormType = itemType === 1 || itemType === "1";
        const isDlType = itemDlType === 1 || itemDlType === "1";

        // ダウンロードフォームの判定改善:
        // 1. dl_type=1 が明示的に設定されている
        // 2. titleに「ダウンロード」「download」が含まれている
        // 3. または link があり、かつ page データがある（フォーム付きダウンロード）
        const isTitleDownload = itemTitle.includes("ダウンロード") || itemTitle.toLowerCase().includes("download");
        const isDownloadForm = isDlType || isTitleDownload || (itemLink && itemPage && itemPage.length > 0 && !isFormType);

        // download_filesがある場合は複数ファイルダウンロード
        const itemDownloadFiles = (item as any).download_files;
        const hasDownloadFiles = Array.isArray(itemDownloadFiles) && itemDownloadFiles.length > 0;

        // linkがある場合は新しいタブで開く
        const hasLink = (item as any).link && (item as any).link.trim() !== "";

        // クリック処理の優先順位（改善版）:
        // 1. dl_type=1 → ダウンロードフォーム表示（最優先: メール形式DL）
        // 2. hasDownloadFiles → 複数ファイル直接ダウンロード（そのままDL）
        // 3. isDownloadForm (link+page) → ダウンロードフォーム表示
        // 4. type=1 → 通常フォーム表示
        // 5. linkがある → 新しいタブで開く（直接リンク）
        // 6. その他 → デフォルト動作
        let onClick: () => void;
        if (isDlType) {
          // dl_type=1: メール形式DL → フォーム表示（最優先）
          onClick = () => handleDownLoad(index);
        } else if (hasDownloadFiles) {
          // 複数ファイル直接ダウンロード（そのままDL）
          onClick = () => downloadCtaFiles(itemDownloadFiles, baseUrl);
        } else if (isDownloadForm) {
          // ダウンロードフォーム表示
          onClick = () => handleDownLoad(index);
        } else if (isFormType) {
          // type=1: 通常フォーム表示
          onClick = () => setForm(index);
        } else if (hasLink) {
          // link: 直接リンクで開く
          onClick = () => handleLinkClick((item as any).link);
        } else {
          // その他: デフォルトでフォーム表示
          onClick = () => setForm(index);
        }

        return (
          <Button
            key={index}
            onClick={onClick}
            className={styles.formButton}
            style={{
              opacity: isImageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              pointerEvents: isImageLoaded ? 'auto' : 'none'
            }}
          >
            {hasImage ? (
              <img
                src={imageSrc}
                alt={item.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                  display: 'block'
                }}
              />
            ) : (
              item.title
            )}
          </Button>
        );
      })}
      </div>
    </div>
  );
}
